/**
 * @file add-order-to-plan.tsx
 * @module app/modals/add-order-to-plan
 * @description Renders the add order to plan modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import CrossSvg from '../../_admin/assets/media/close.png';
import { ordersDelivery, routePlanning } from '../../api/apiEndPoints';
import APICallService from '../../api/apiCallService';
import { useEffect, useState } from 'react';
import { PAGE_LIMIT } from '../../utils/constants';
import Loader from '../../Global/loader';
import Method from '../../utils/methods';
import { routePlanningJSON } from '../../api/apiJSON/routePlanning';
import { KTSVG } from '../../_admin/helpers';
import { getKey } from '../../Global/history';
import { warehouse as warehouseStoreString } from '../../utils/storeString';
import { useWarehouse } from '../context/WarehouseContext';
import { useDebounce } from '../../utils/useDebounce';
import { getOrderItemsByCalculatingStockCountDisplay } from '../modules/orders-delivery/utils/quantity';
import { getOrderWeightByCalculatingQuantityWeightsDisplay } from '../modules/orders-delivery/utils/weight';
import { MoveToPendingSpan } from '../modules/_common/components/move-to-pending-span';
import {
  OrderPlacedByCustomer,
  RouteOrderMovedToPendingOrder,
} from '../../utils/constants';
import OrderBusinessStatus from '../modules/_common/components/order-business-status';

const AddOrderPlan = (props: any) => {
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set());
  const [partialIds, setPartialIds] = useState<Set<string>>(new Set());
  const [partialLoading, setPartialLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<any>(-1);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [page, setPage] = useState<any>(1);
  const [pageLimit, setPageLimit] = useState<any>(PAGE_LIMIT);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selected, setSelected] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<any>([]);
  useEffect(() => {
    (async () => {
      await fetchDetails([1], search);
    })();
  }, []);
  const fetchDetails = async (status?: any, searchTerm?: string) => {
    setFetchLoader(true);
    let params: any = {
      sortKey: '_createdAt',
      sortOrder: -1,
      needCount: true,
      searchTerm: searchTerm,
      fromDate: Method.getDateBeforeDays(
        Method.getTodayDate('YYYY-MM-DD'),
        27,
        'YYYY-MM-DD'
      ),
      toDate: Method.getTodayDate('YYYY-MM-DD'),
      // ward:
      //   area && area != -1 ? (area != -2 ? encodeURIComponent(area) : -1) : "",
    };

    if (props?.districtIds && Array?.isArray(props.districtIds)) {
      props?.districtIds?.forEach((id: any, index: any) => {
        params[`district\\[${index}\\]`] = id;
      });
    }

    if (status.length > 0) {
      status.map((val: any) => {
        params = { ...params, listType: val };
      });
    }
    if (selectedWarehouse?.value) {
      params = { ...params, warehouse: selectedWarehouse?.value };
    }
    let endpoint = ordersDelivery.list;
    // Accumulate results from all API calls
    let accumulatedOrders: any[] = [];
    const districtReferences = props.vehicle.ward?.map(
      (item: any) => item.district.reference
    );
    let districts = [...new Set(districtReferences)];
    let apiParams = {
      ...params,
    };
    if (districts.length > 0) {
      districts.map((val: any, index: number) => {
        apiParams = { ...apiParams, ['district\\[' + index + '\\]']: val };
      });
    }
    let apiService = new APICallService(
      endpoint,
      apiParams,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value
    );
    let response: any = await apiService.callAPI();

    if (response.records) {
      accumulatedOrders = [...accumulatedOrders, ...response.records]; // Accumulate the records
    }

    if (accumulatedOrders?.length > 0) {
      setOrders(accumulatedOrders); // Set the combined orders data
      if (accumulatedOrders?.length < params.limit) {
        setTotalRecords(accumulatedOrders.length);
      } else {
        setTotalRecords(accumulatedOrders.length); // You can set total count based on the API response or logic
      }
    } else {
      setOrders([]); // If no orders are found, clear the list
      setTotalRecords(0);
    }
    setFetchLoader(false);
  };
  const kgToTons = (kilograms: number) => {
    // Conversion factor
    const conversionFactor = 1000;
    // Calculate metric tons
    const tons = kilograms / conversionFactor;
    return tons.toFixed(3);
  };
  const handleAddOrderToList = async (data: any, index: number) => {
    setAddingIds((prev) => new Set(prev).add(data));
    let params = {
      order: [data],
    };
    let apiService = new APICallService(
      routePlanning.addOrder,
      routePlanningJSON.addOrder(params),
      { routeId: props.routeId, vehicleId: props.vehicleId },
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      setOrders((prev: any) => prev.filter((o: any) => o._id !== data));
      setSelectedId((prev: any) => [...prev, data]);
    }
    setAddingIds((prev) => {
      const next = new Set(prev);
      next.delete(data);
      return next;
    });
  };
  const handlePartialOrderToList = async (data: any, index: number) => {
    setPartialIds((prev) => new Set(prev).add(data));
    let params = {
      order: [data],
    };
    let apiService = new APICallService(
      routePlanning.addPartialOrder,
      routePlanningJSON.addOrder(params),
      { routeId: props.routeId, vehicleId: props.vehicleId },
      '',
      '',
      '',
      props.moduleName
    );
    let response: any = await apiService.callAPI();
    if (response) {
      setOrders((prev: any) => prev.filter((o: any) => o._id !== data));
      setSelectedId((prev: any) => [...prev, data]);
    }
    setPartialIds((prev) => {
      const next = new Set(prev);
      next.delete(data);
      return next;
    });
  };
  const handleRemoveOrderFromList = async (data: any, index: number) => {
    setLoading(true);
    setCurrentIndex(index);
    let params = {
      order: data,
    };
    let apiService = new APICallService(
      routePlanning.removeOrder,
      routePlanningJSON.addOrder(params),
      { routeId: props.routeId, vehicleId: props.vehicleId },
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      let temp: any = [...selectedId];
      temp = temp.filter((id: any) => id !== data);
      setSelectedId(temp);
    }
    setCurrentIndex(-1);
    setLoading(false);
  };

  const debounce = useDebounce(fetchDetails, 500);
  const handleSearch = async (value: string) => {
    setSearch(value);
    setFetchLoader(true);
    await debounce([1], value);
    setFetchLoader(false);
  };

  const isTruthyPharmacyOrder = (value: any) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' ||
        normalized === '1' ||
        normalized === 'yes' ||
        normalized === 'y'
      );
    }
    return false;
  };

  const getOrderTypeLabel = (orderVal: any) => {
    const isPharmacyOrder = isTruthyPharmacyOrder(
      orderVal?.isPharmacyOrder ?? orderVal?.postData?.isPharmacyOrder
    );
    return isPharmacyOrder ? 'Pharmacy' : 'Utrade';
  };

  /**
   * Checks if an order record is a pharmacy order based on its properties.
   * @param {any} orderVal - The order record to check.
   * @returns {boolean} - True if the order is a pharmacy order, false otherwise.
   */
  const isPharmacyOrderRecord = (orderVal: any) => {
    const directOrderValue =
      orderVal?.isPharmacyOrder ?? orderVal?.order?.isPharmacyOrder;
    return isTruthyPharmacyOrder(directOrderValue);
  };

  const showIfPharmacySpan = (orderVal: any) => {
    return isPharmacyOrderRecord(orderVal)
      ? `<span className='badge badge-success'>{getOrderTypeLabel(orderVal)}</span>`
      : ``;
  };

  const filteredOrders = orders.filter((orderVal: any) => {
    const matchesOrderType =
      !orderTypeFilter ||
      (orderTypeFilter === 'pharmacy' &&
        getOrderTypeLabel(orderVal) === 'Pharmacy') ||
      (orderTypeFilter === 'utrade' &&
        getOrderTypeLabel(orderVal) === 'Utrade');

    const searchTerm = search.trim().toLowerCase();
    const matchesSearch =
      !searchTerm ||
      orderVal?.refKey?.toLowerCase().includes(searchTerm) ||
      orderVal?.customer?.name?.toLowerCase().includes(searchTerm);

    return matchesOrderType && matchesSearch;
  });

  return (
    <Modal
      {...props}
      show={props.show}
      onHide={() => {
        props.onHide(selectedId);
      }}
      dialogClassName="modal-dialog-centered min-w-lg-1000px min-w-md-1000px"
      className="border-r10px"
      centered
    >
      <Modal.Header>
        <Row className="fs-18 fw-bolder w-100">
          <Col lg={12} className="d-flex pe-0">
            <Col className="mb-md-0 mb-5 d-flex">
              <div className="d-flex align-items-center">Orders List</div>
            </Col>
            <Col
              md
              className="mb-md-0 mb-5 d-flex justify-content-end align-items-center gap-3"
            >
              <div className="d-flex align-items-center">
                <select
                  className="form-select form-select-solid form-select-lg min-h-40px"
                  value={orderTypeFilter}
                  onChange={(event: any) =>
                    setOrderTypeFilter(event.target.value)
                  }
                >
                  <option value="">Order Type</option>
                  <option value="pharmacy">Pharmacy</option>
                  <option value="utrade">Utrade</option>
                </select>
              </div>
              <div className="d-flex align-items-center mw-lg-400px position-relative">
                <KTSVG
                  path="/media/icons/duotune/general/gen021.svg"
                  className="svg-icon-3 position-absolute ms-3"
                />
                <input
                  type="text"
                  id="kt_filter_search"
                  className="form-control form-control-white min-h-40px form-control-lg ps-10"
                  placeholder="Search by order ID"
                  value={search}
                  onChange={(event: any) => {
                    handleSearch(event.target.value.trimStart());
                  }}
                />
              </div>
            </Col>
          </Col>
        </Row>
      </Modal.Header>
      <Modal.Body>
        <div className="mh-300px scroll-y me-n7 pe-7">
          {fetchLoader ? (
            <>
              <div className="d-flex flex-stack py-4">
                <div className="d-flex justify-content-center">
                  {' '}
                  <Loader loading={fetchLoader} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-rounded table-bordered align-middle">
                  <thead>
                    <tr className="fw-bold fs-14 fw-600 text-dark border-bottom align-middle">
                      <th className="col-w-30">Order</th>
                      <th className="col-w-20">Customer</th>
                      <th className="col-w-10">Quantity</th>
                      <th className="col-w-10">Weight</th>
                      <th className="col-w-10">Amount</th>
                      <th className="text-end col-w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length ? (
                      filteredOrders.map(
                        (orderVal: any, orderIndex: number) => (
                          <tr key={orderIndex}>
                            <td className="fs-12 fw-500">
                              <div className="form-check form-check-sm form-check-custom form-check-solid">
                                <div className="d-flex align-items-start flex-column">
                                  <span className="text-dark d-block f-14 fw-500">
                                    {orderVal.refKey}
                                    {showIfPharmacySpan(orderVal)}
                                  </span>
                                  <span className="text-gray d-block">
                                    {Method.convertDateToDDMMYYYYHOURS(
                                      orderVal._createdAt
                                    )}
                                  </span>
                                  <OrderBusinessStatus
                                    isNewOrder={orderVal?.isNewOrder}
                                    orderVal={orderVal}
                                  />
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="fs-12 fw-500 d-block">
                                {orderVal.customer.name}
                              </span>
                            </td>
                            <td aria-description="Quantity column">
                              {getOrderItemsByCalculatingStockCountDisplay(
                                orderVal,
                                'fs-12 fw-500'
                              )}
                            </td>
                            <td aria-description="Weight column">
                              {getOrderWeightByCalculatingQuantityWeightsDisplay(
                                orderVal,
                                'fs-12 fw-500'
                              )}
                            </td>
                            <td>
                              <span className="fs-12 fw-500">
                                {'TSh ' +
                                  Method.getGeneralizedAmount(
                                    orderVal.payment.totalCharge
                                  )}
                              </span>
                            </td>
                            <td className="text-center">
                              <>
                                <div className="d-flex justify-content-center">
                                  <Button
                                    className="uw-btn uw-btn-primary"
                                    disabled={
                                      addingIds.has(orderVal._id) ||
                                      selectedId.includes(orderVal._id)
                                    }
                                    onClick={() =>
                                      handleAddOrderToList(
                                        orderVal._id,
                                        orderIndex
                                      )
                                    }
                                  >
                                    {addingIds.has(orderVal._id) && (
                                      <span
                                        className="indicator-progress"
                                        style={{
                                          display: 'block',
                                        }}
                                      >
                                        Please wait...
                                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                      </span>
                                    )}
                                    {!addingIds.has(orderVal._id) && <>Add</>}
                                  </Button>

                                  <Button
                                    className="uw-btn uw-btn-primary"
                                    disabled={
                                      partialIds.has(orderVal._id) ||
                                      selectedId.includes(orderVal._id)
                                    }
                                    onClick={() =>
                                      handlePartialOrderToList(
                                        orderVal._id,
                                        orderIndex
                                      )
                                    }
                                  >
                                    {partialIds.has(orderVal._id) && (
                                      <span
                                        className="indicator-progress"
                                        style={{
                                          display: 'block',
                                        }}
                                      >
                                        Please wait...
                                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                      </span>
                                    )}
                                    {!partialIds.has(orderVal._id) && (
                                      <>Partial Order</>
                                    )}
                                  </Button>
                                </div>
                              </>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <>
                        <tr>
                          <td colSpan={6}>
                            <div className="w-100 fs-14 fw-bold d-flex justify-content-center">
                              No Data found
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
export default AddOrderPlan;
